import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Iconify from '../iconify';
import { sendPasswordReset } from '../../service/auth';
import {
  authBackButtonSx,
  authDialogPaperSx,
  authFieldSx,
  authFormSx,
  authPrimaryButtonSx,
} from './modalLayout';

const ResetSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Required'),
});

/**
 * Request a password reset link.
 *
 * Terminal branch of the auth flow: it never produces a signed-in user, so
 * it takes no onAccountCreated. The backend emails a branded reset from
 * office@kbtla.org; the member sets the new password on /reset-password.
 *
 * The confirmation deliberately does not say whether the address had an
 * account — see PASSWORD_RESET_NOTICE in service/auth.js.
 */
export default function ForgotPasswordModal({ open, handleClose, initialEmail = '', onBackToSignIn }) {
  const [sent, setSent] = React.useState(false);
  const emailInputRef = React.useRef(null);

  // A fresh open starts over, including after a previous send. The focus
  // hop matches EmailSignUpModal and PhoneVerificationModal, which both put
  // the caret in their first field on open.
  React.useEffect(() => {
    if (open) {
      setSent(false);
      setTimeout(() => emailInputRef.current?.focus(), 100);
    }
  }, [open]);

  const onSubmit = async (values, { setSubmitting }) => {
    const ok = await sendPasswordReset(values.email);
    if (ok) setSent(true);
    setSubmitting(false);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="forgot-password-title"
      aria-describedby="forgot-password-description"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Box
        role="dialog"
        aria-modal="true"
        aria-labelledby="forgot-password-title"
        aria-describedby="forgot-password-description"
        data-testid="forgot-password-modal"
        sx={authDialogPaperSx}
      >
        <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <IconButton
            onClick={onBackToSignIn}
            aria-label="Back to sign in"
            data-testid="forgot-back-to-signin"
            sx={authBackButtonSx}
          >
            <Iconify icon="eva:arrow-back-fill" width={16} sx={{ color: '#374151' }} />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, pb: 3 }}>
          <Typography
            id="forgot-password-title"
            sx={{ fontSize: '20px', fontWeight: 700, color: '#111827', textAlign: 'center' }}
          >
            Reset your password
          </Typography>

          {sent ? (
            <Box sx={{ mt: 2.5, textAlign: 'center' }} role="status" aria-live="polite">
              <Iconify icon="eva:email-outline" width={40} sx={{ color: '#242258' }} />
              <Typography sx={{ fontSize: '14px', color: '#374151', mt: 1.5, lineHeight: 1.6 }}>
                If an account exists for that address, we&apos;ve sent a reset link from
                Kehilat Bnei Torah. Check your inbox.
              </Typography>
              <Button
                onClick={handleClose}
                data-testid="forgot-done"
                fullWidth
                sx={{
                  mt: 2.5,
                  ...authPrimaryButtonSx,
                }}
              >
                Done
              </Button>
            </Box>
          ) : (
            <>
              <Typography
                id="forgot-password-description"
                sx={{ fontSize: '14px', color: '#6B7280', mt: 0.5, mb: 2.5, textAlign: 'center', lineHeight: 1.6 }}
              >
                Enter your email and we&apos;ll send you a link to set a new one.
              </Typography>

              <Formik
                initialValues={{ email: initialEmail }}
                enableReinitialize
                validationSchema={ResetSchema}
                onSubmit={onSubmit}
              >
                {({ handleChange, handleSubmit, touched, errors, values, isSubmitting }) => (
                  <Box component="form" onSubmit={handleSubmit} noValidate sx={authFormSx}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#374151', mb: 0.5 }}>
                      Email Address
                    </Typography>
                    <TextField
                      name="email"
                      type="email"
                      placeholder="john.doe@example.com"
                      value={values.email}
                      onChange={handleChange}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      aria-label="Email address"
                      autoComplete="email"
                      inputRef={emailInputRef}
                      inputProps={{ 'data-testid': 'forgot-email' }}
                      fullWidth
                      size="small"
                      sx={authFieldSx}
                    />

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      fullWidth
                      data-testid="forgot-submit"
                      sx={{
                        mt: 2.5,
                        ...authPrimaryButtonSx,
                      }}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={18} sx={{ color: '#fff' }} />
                      ) : (
                        'Send reset link'
                      )}
                    </Button>
                  </Box>
                )}
              </Formik>
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
}
